import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { Alert } from 'antd'
import { isArray } from 'lodash'
import { faPlus } from '@fortawesome/pro-solid-svg-icons'

import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../../reducers/UserReducer'
import IconButton from '../../../antd/Buttons/IconButton'
import Loading from '../../Loading'
import SchoolModal from '../../../../Providers/Forms/SchoolModal'
import SchoolsSelector from './SchoolsSelector'
import { School } from '../../../../utils/entities/school'
import { getAllSchools } from '../../../../utils/api/school'
import { formatAsArray } from '../../../../utils'

const PAGE_SIZE = 25
const SEARCH_DEBOUNCE = 200

const ExtendedSchoolsSelector = ({
  className = '', disabled, dropdownDisabled, multiple = 'default', reverse = false, style = {}, values, onChange, renderOption
}) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [schoolEntities, setSchoolEntities] = useState([])
  const [moreSchoolsCount, setMoreSchoolsCount] = useState(0)
  const [searchValue, setSearchValue] = useState('')
  const [debounceSearch, setDebounceSearch] = useState('')
  const [loadingSchools, setLoadingSchools] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)

  /** The selected schools are requested on top of the page, so that the current value stays displayable. */
  const additionalIds = (isArray(values) ? values : [values]).filter(id => id !== null && id !== undefined)

  /** Depended upon instead of the array itself : a parent rebuilding its array would loop the fetch. */
  const additionalIdsKey = additionalIds.join(',')

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounceSearch(searchValue)
    }, SEARCH_DEBOUNCE)

    return () => clearTimeout(timer)
  }, [searchValue])

  useEffect(() => {
    if (!user) {
      return
    }

    const loadingTimer = setTimeout(() => setLoadingSchools(true), SEARCH_DEBOUNCE)

    /** Without `paginate`, the API short circuits on a branch that ignores the search and returns every school. */
    const parameters = { paginate: true, page: 1, pageSize: PAGE_SIZE, search: debounceSearch, additionalIds }

    getAllSchools(user, parameters).then(({ data, meta }) => {
      setSchoolEntities(formatAsArray(data))
      setMoreSchoolsCount(meta?.pages?.total ? Math.max(meta.pages.total - PAGE_SIZE, 0) : 0)
    }).catch(() => {
      setSchoolEntities([])
      setMoreSchoolsCount(0)
    }).finally(() => {
      clearTimeout(loadingTimer)
      setLoadingSchools(false)
    })
  }, [user, debounceSearch, additionalIdsKey, reloadToken])

  /** A school created from the dropdown belongs to the list right away : refetch so that it shows up. */
  const handleSchoolSubmit = school => school && setReloadToken(token => token + 1)

  const renderDropdown = onClick => menu => (
    <div>
      {moreSchoolsCount > 0 &&
        <Alert
          className='more-message'
          message={t('form.request.institutions_count_more', { count: moreSchoolsCount })}
          type='info'
          showIcon
          banner
        />}
      {loadingSchools ? (
        <div className='p-10'>
          <Loading loading={loadingSchools} style={{ height: '100px' }} />
        </div>
      ) : menu}
      <div className='flex-col m-4' onMouseDown={e => e.preventDefault()}>
        <IconButton
          text={t('form.request.new_school')}
          icon={faPlus}
          onClick={() => onClick(handler => ({ ...handler, data: new School(), callback: handleSchoolSubmit }))}
        />
      </div>
    </div>
  )

  return (
    <SchoolModal>
      <SchoolsSelector
        className={className}
        disabled={disabled}
        multiple={multiple}
        reverse={reverse}
        schools={schoolEntities}
        style={style}
        values={values}
        dropdownRender={!dropdownDisabled && renderDropdown}
        onChange={onChange}
        renderOption={renderOption}
        loading={loadingSchools}
        onSearch={value => setSearchValue(value)}
      />
    </SchoolModal>
  )
}

export default ExtendedSchoolsSelector
